/** @jsxImportSource @emotion/react */
import { TruncatedString } from '@quickbit/backoffice-core-frontend';
import {
  UtilityButton,
  List,
  ListItem,
  Modal,
  Typography,
  useTheme,
  removeScaleFromMoney,
} from '@quickbit/qb-design-system';
import { useCallback } from 'react';
import { useAsync } from 'react-use';
import { JsonView, QBLoader } from 'components';
import { getOrder } from '../api';
import { OrderMinimal } from '../types';
import { RetryActions } from './RetryActions';

interface Props {
  minimalOrder: OrderMinimal;
  deselect: () => void;
}

export const OrderDetails = ({ minimalOrder, deselect }: Props) => {
  const { spacing, sizing, palette } = useTheme();

  const { value: order, loading } = useAsync(() => {
    return getOrder(minimalOrder.uuid);
  }, [minimalOrder.uuid]);

  const openOrderInGrafana = useCallback(() => {
    const environment =
      process.env.REACT_APP_ENVIRONMENT === 'production' ? 'prod' : 'test';
    const startTime = order?.createdAt ? order.createdAt.getTime() : 'now-1h';
    const endTime = startTime === 'now-1h' ? 'now' : startTime + 1000 * 60 * 60;
    const link = `https://grafana.quickbit.com/explore?schemaVersion=1&panes=%7B%22wvr%22:%7B%22datasource%22:%22loki%22,%22queries%22:%5B%7B%22refId%22:%22A%22,%22expr%22:%22%20%7Bapp%3D~%5C%22app-backend-merchant-worker%7Capp-backend-worker%5C%22,%20environment%3D%5C%22${environment}%5C%22%7D%20%7C%20json%20%7C~%5C%22${order?.uuid}%5C%22%22,%22queryType%22:%22range%22,%22datasource%22:%7B%22type%22:%22loki%22,%22uid%22:%22loki%22%7D,%22editorMode%22:%22code%22%7D%5D,%22range%22:%7B%22from%22:%22${startTime}%22,%22to%22:%22${endTime}%22%7D%7D%7D&orgId=1`;
    window.open(link, '_blank');
  }, [order]);

  return (
    <Modal
      title="Order details"
      secondaryButtonProps={{
        onClick: deselect,
        text: 'Close',
      }}
      closeModal={deselect}
    >
      {(loading || !order) && <QBLoader />}
      {order && (
        <>
          <div
            css={{
              overflow: 'scroll',
              maxHeight: '80vh',
              minWidth: sizing.large,
            }}
          >
            <div
              css={{
                marginBottom: spacing.xxs,
              }}
            >
              <List>
                <ListItem
                  title="Order ID"
                  subcontent={<TruncatedString text={order.uuid} />}
                />
                <ListItem
                  title="Order amount"
                  content={`${order.amount} ${order.currency}`}
                />
                <ListItem
                  title="Creation amount"
                  content={`${order.creationAmount} ${order.currency}`}
                />
                {order.cryptoAmount && (
                  <ListItem
                    title="Crypto amount"
                    content={`${removeScaleFromMoney(order.cryptoAmount)} ${
                      order.cryptoAmount.currencyCode
                    }`}
                  />
                )}
                {order.purchasedAt && (
                  <ListItem
                    title="Purchased at"
                    content={order.purchasedAt.toLocaleString()}
                  />
                )}
                {order.referenceId && (
                  <ListItem
                    title="Reference ID"
                    content={order.referenceId.toLocaleString()}
                  />
                )}
                {order.merchant.uuid && (
                  <ListItem
                    title="Merchant"
                    subcontent={
                      <UtilityButton
                        variant="text"
                        text={order.merchant.name}
                        onClick={() => {
                          window.open(
                            `/merchants/${order.merchant.uuid}`,
                            '_blank'
                          );
                        }}
                      />
                    }
                  />
                )}
                {order.userId && (
                  <ListItem
                    title={
                      <UtilityButton
                        variant="text"
                        text="User Id"
                        onClick={() => {
                          window.open(
                            `/users/${
                              order.useProcessingFlow ? 'processing' : 'app'
                            }/${order.userId}`,
                            '_blank'
                          );
                        }}
                      />
                    }
                    subcontent={<TruncatedString text={order.userId} />}
                  />
                )}
                {order.email && (
                  <ListItem title="Email" content={order.email} />
                )}
                {order.sendOrderPreviewId && (
                  <ListItem
                    title="Send order preview ID"
                    subcontent={
                      <TruncatedString text={order.sendOrderPreviewId} />
                    }
                  />
                )}
                {order.receiveOrderPreviewId && (
                  <ListItem
                    title="Receive order preview ID"
                    subcontent={
                      <TruncatedString text={order.receiveOrderPreviewId} />
                    }
                  />
                )}
                {order.address && !order.useProcessingFlow && (
                  <ListItem
                    title="Address"
                    subcontent={<TruncatedString text={order.address} />}
                  />
                )}
                {order.redirectUrl && (
                  <ListItem
                    title="Redirect Url"
                    subcontent={
                      <TruncatedString
                        text={order.redirectUrl}
                        type="end"
                        split={40}
                      />
                    }
                  />
                )}
                {order.failureRedirectUrl && (
                  <ListItem
                    title="Failure Redirect Url"
                    subcontent={
                      <TruncatedString
                        text={order.failureRedirectUrl}
                        type="end"
                        split={40}
                      />
                    }
                  />
                )}
                {order.refundRedirectUrl && (
                  <ListItem
                    title="Refund Redirect Url"
                    subcontent={
                      <TruncatedString
                        text={order.refundRedirectUrl}
                        type="end"
                        split={40}
                      />
                    }
                  />
                )}
                <>
                  {order.extraData?.blockchainTransactionId &&
                    typeof order.extraData.blockchainTransactionId ===
                      'string' && (
                      <ListItem
                        title="Blockchain transaction id"
                        subcontent={
                          <TruncatedString
                            text={order.extraData.blockchainTransactionId}
                          />
                        }
                      />
                    )}
                  {order.extraData?.maskedCreditCardNumber &&
                    typeof order.extraData.maskedCreditCardNumber ===
                      'string' && (
                      <ListItem
                        title="Masked credit card number"
                        content={order.extraData.maskedCreditCardNumber}
                      />
                    )}
                  {order.extraData?.creditCardAccountHolder &&
                    typeof order.extraData.creditCardAccountHolder ===
                      'string' && (
                      <ListItem
                        title="Credit card account holder"
                        content={order.extraData.creditCardAccountHolder}
                      />
                    )}
                  {order.extraData?.issuerName &&
                    typeof order.extraData.issuerName === 'string' && (
                      <ListItem
                        title="Issuer Name"
                        content={order.extraData.issuerName}
                      />
                    )}
                  {order.extraData?.ssn &&
                    typeof order.extraData.ssn === 'string' && (
                      <ListItem
                        title="Customer SSN"
                        content={order.extraData.ssn}
                      />
                    )}
                  {order.extraData?.countryCode &&
                    typeof order.extraData.countryCode === 'string' && (
                      <ListItem
                        title="Customer Country"
                        content={order.extraData.countryCode}
                      />
                    )}
                </>
                {order.useOnrampFlow && (
                  <ListItem
                    title="Has User Data"
                    content={order.hasUserData ? 'Yes' : 'No'}
                  />
                )}
                {order.receivedRatio && (
                  <ListItem
                    title="Received ratio"
                    content={order.receivedRatio.toString()}
                  />
                )}
                <ListItem
                  title="Order type"
                  content={
                    order.useProcessingFlow
                      ? 'Processing'
                      : order.useOnrampFlow
                      ? 'Onramp'
                      : 'Pay'
                  }
                />
              </List>
            </div>
            <div
              css={{
                marginBottom: spacing.xxs,
              }}
            >
              <RetryActions orders={[minimalOrder]} />
            </div>
            {order.settlement && (
              <div css={{ marginBottom: spacing.xxs }}>
                <Typography variant="bodyBold">Settlement</Typography>
                <List>
                  <ListItem
                    title="Settled Amount"
                    content={`€ ${order.settlement.euroAmount}`}
                  />
                  <ListItem
                    title="Processing Fee"
                    content={`€ ${order.settlement.euroFee}`}
                  />
                  <ListItem
                    title="Customer Fee"
                    content={`€ ${order.settlement.euroCustomerFee}`}
                  />
                </List>
              </div>
            )}
            {order.refund && order.status === 'REFUNDED' && (
              <div css={{ marginBottom: spacing.xxs }}>
                <Typography variant="bodyBold">Refund</Typography>
                <List>
                  <ListItem
                    title="Received amount"
                    content={order.refund.receivedAmount}
                    subtitle="Refund address"
                    subcontent={order.refund.destinationAddress}
                  />
                </List>
              </div>
            )}
            {order.manualRefunds && (
              <div css={{ marginBottom: spacing.xxs }}>
                <Typography variant="bodyBold">Manual Refunds</Typography>
                <List>
                  {order.manualRefunds.map((manualRefund) => (
                    <ListItem
                      key={manualRefund.createdAt.getTime()}
                      title="Refund amount"
                      content={manualRefund.refundAmount?.toFixed(2) ?? '-'}
                      subtitle={
                        <div css={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography
                            variant="bodySmall"
                            color={palette.grey.default}
                          >
                            Refund address
                          </Typography>
                          <Typography
                            variant="bodySmall"
                            color={palette.grey.default}
                          >
                            Refund transaction ID
                          </Typography>
                          <Typography
                            variant="bodySmall"
                            color={palette.grey.default}
                          >
                            Created At
                          </Typography>
                        </div>
                      }
                      subcontent={
                        <div css={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography
                            variant="bodySmall"
                            color={palette.grey.default}
                          >
                            {manualRefund.destinationAddress ?? '-'}
                          </Typography>
                          <Typography
                            variant="bodySmall"
                            color={palette.grey.default}
                          >
                            {manualRefund.refundTransactionId ?? '-'}
                          </Typography>
                          <Typography
                            variant="bodySmall"
                            color={palette.grey.default}
                          >
                            {manualRefund.createdAt.toLocaleString()}
                          </Typography>
                        </div>
                      }
                    />
                  ))}
                </List>
              </div>
            )}
            {order.errors.length > 0 && (
              <div css={{ marginBottom: spacing.xxs }}>
                <Typography variant="bodyBold">Errors</Typography>
                <div css={{ overflowY: 'scroll', maxHeight: '30vh' }}>
                  <List>
                    {order.errors.map((e, i) => (
                      <div key={i} css={{ padding: spacing.xxs }}>
                        <Typography variant="bodySmallBold">
                          {e.errorCode}
                        </Typography>
                        <Typography variant="bodySmallest">
                          {`At ${e.createdAt.toLocaleString()}`}
                        </Typography>
                        <JsonView data={e.details} />
                      </div>
                    ))}
                  </List>
                </div>
              </div>
            )}
            {order.callbacks.length > 0 && (
              <div css={{ marginBottom: spacing.xxs }}>
                <span>
                  <Typography variant="bodyBold">Callbacks</Typography>
                  <Typography variant="bodySmallest">
                    {order.callbackUrl}
                  </Typography>
                </span>
                <div css={{ overflowY: 'scroll', maxHeight: '30vh' }}>
                  <List>
                    {order.callbacks.map((c, i) => (
                      <div key={i} css={{ padding: spacing.xxs }}>
                        <span
                          css={{
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography
                            variant="bodySmallBold"
                            color={
                              c.ok === null
                                ? undefined
                                : c.ok
                                ? palette.success.x500
                                : palette.danger.x500
                            }
                          >
                            {c.ok === null
                              ? 'Processing'
                              : c.ok
                              ? 'Success'
                              : 'Failed'}
                          </Typography>
                          <Typography variant="bodySmallest">
                            {`At ${c.createdAt.toLocaleString()}`}
                          </Typography>
                        </span>
                        <Typography variant="bodySmall">
                          Request body:
                        </Typography>
                        <JsonView data={c.requestBody} />
                        {c.responseContent && (
                          <>
                            <Typography variant="bodySmall">
                              Response:
                            </Typography>
                            <pre>{c.responseContent}</pre>
                          </>
                        )}
                      </div>
                    ))}
                  </List>
                </div>
              </div>
            )}
            {order.history.length > 0 && (
              <div css={{ marginBottom: spacing.xxs }}>
                <Typography variant="bodyBold">History</Typography>
                <div css={{ overflowY: 'scroll', maxHeight: '30vh' }}>
                  <List>
                    {order.history
                      .sort(
                        (a, b) => a.createdAt.getTime() - b.createdAt.getTime()
                      )
                      .map((h) => (
                        <ListItem
                          key={h.createdAt.getTime()}
                          title="Status"
                          content={h.status}
                          subtitle="At"
                          subcontent={h.createdAt.toLocaleString()}
                        />
                      ))}
                  </List>
                </div>
              </div>
            )}
          </div>
          <div
            css={{
              marginBottom: spacing.xxs,
              marginTop: spacing.xxs,
            }}
          >
            <UtilityButton
              text="Open Order in Grafana"
              onClick={openOrderInGrafana}
            />
          </div>
        </>
      )}
    </Modal>
  );
};
